import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { WARMUP_DURATIONS_SEC, WARMUP_START_PCT, WPM_RANGE } from '@vif/core';
import { APP_VERSION } from '../app/version.js';
import { FontSelect } from '../components/FontSelect.jsx';
import { useServices } from '../app/services.js';
import { Modal } from '../components/Modal.jsx';
import {
  BackupError,
  BackupTooLargeError,
  exportBackup,
  getSettings,
  importBackup,
  listSessions,
  type SessionRow,
} from '../storage/index.js';

interface Totals {
  readonly words: number;
  readonly timeMs: number;
  readonly sessions: number;
  readonly last: SessionRow | undefined;
}

export function SettingsView(): ReactNode {
  const { db, t, settings, updateSettings } = useServices();
  const navigate = useNavigate();
  const [totals, setTotals] = useState<Totals | null>(null);
  const [importConfirm, setImportConfirm] = useState<File | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const backupInput = useRef<HTMLInputElement>(null);

  // H3: Esc closes settings. The import-confirm Modal handles Esc in the
  // capture phase and stops propagation, so this only fires when no modal
  // is open.
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') void navigate('/library');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);

  useEffect(() => {
    void (async () => {
      const sessions = await listSessions(db);
      const stored = await getSettings(db);
      const legacy = (stored.data['legacyTotals'] ?? {}) as {
        totalWords?: number;
        totalTime?: number;
        totalSessions?: number;
      };
      const last = sessions.reduce<SessionRow | undefined>(
        (latest, row) => (latest === undefined || row.date >= latest.date ? row : latest),
        undefined,
      );
      setTotals({
        words: sessions.reduce((n, s) => n + s.words, 0) + (legacy.totalWords ?? 0),
        timeMs: sessions.reduce((n, s) => n + s.activeMs, 0) + (legacy.totalTime ?? 0) * 1000,
        sessions: sessions.length + (legacy.totalSessions ?? 0),
        last,
      });
    })();
  }, [db]);

  const showToast = (message: string): void => {
    setToast(message);
    window.setTimeout(() => setToast(null), 3200);
  };

  const doExport = async (): Promise<void> => {
    let bytes: Uint8Array;
    try {
      // W-4 grandfather: export refuses to emit an archive restore would reject,
      // throwing a BackupTooLargeError that names the offending document.
      bytes = await exportBackup(db, { appVersion: APP_VERSION });
    } catch (error) {
      showToast(error instanceof BackupTooLargeError ? error.message : String(error));
      return;
    }
    const url = URL.createObjectURL(new Blob([bytes as BlobPart], { type: 'application/zip' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `vif-backup-${new Date().toISOString().slice(0, 10)}.vif`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const doImport = async (file: File): Promise<void> => {
    try {
      await importBackup(db, new Uint8Array(await file.arrayBuffer()));
      showToast(t('settings.importDone'));
      window.setTimeout(() => window.location.reload(), 800);
    } catch (error) {
      showToast(error instanceof BackupError ? error.message : String(error));
    }
  };

  const minutes = (ms: number): string => {
    const total = Math.round(ms / 60000);
    return total >= 60 ? `${Math.floor(total / 60)}h ${total % 60}m` : `${total}m`;
  };

  return (
    <div className="app">
      <header className="topbar">
        <Link className="brand" to="/library">
          V<em>i</em>f
        </Link>
      </header>
      {/* H3: persistent close — fixed top-right at every scroll position. */}
      <button
        className="settings-close"
        aria-label={t('common.close')}
        onClick={() => void navigate('/library')}
      >
        ✕
      </button>
      <main className="body">
        <div className="settings">
          <h1>{t('settings.title')}</h1>

          {/* Reading */}
          <div className="setting-group">
            <div className="card setting-row">
              <div>
                <div className="label">{t('settings.targetWpm')}</div>
                <div className="hint">
                  {WPM_RANGE.min}–{WPM_RANGE.max}
                </div>
              </div>
              <div className="row-controls">
                <input
                  type="range"
                  aria-label={t('settings.targetWpm')}
                  min={WPM_RANGE.min}
                  max={WPM_RANGE.max}
                  step={25}
                  value={settings.targetWpm}
                  onChange={(e) => updateSettings({ targetWpm: Number(e.target.value) })}
                />
                <span className="wpm" style={{ fontFamily: "'Vif Mono', monospace" }}>
                  {settings.targetWpm}
                </span>
                <button className="btn btn-sm" onClick={() => void navigate('/onboarding')}>
                  {t('settings.recalibrate')}
                </button>
              </div>
            </div>
            <div className="card setting-row">
              <span className="label">{t('settings.adaptive')}</span>
              <Toggle
                value={settings.adaptiveEnabled}
                onChange={(v) => updateSettings({ adaptiveEnabled: v })}
              />
            </div>
            <div className="card setting-row">
              <span className="label">{t('settings.warmup')}</span>
              <Toggle
                value={settings.warmupEnabled}
                onChange={(v) => updateSettings({ warmupEnabled: v })}
              />
            </div>
            {settings.warmupEnabled && (
              <div className="card setting-row">
                <span className="label">{t('settings.warmupDuration')}</span>
                <div className="seg">
                  {WARMUP_DURATIONS_SEC.map((sec) => (
                    <button
                      key={sec}
                      className={settings.warmupDurationSec === sec ? 'active' : ''}
                      onClick={() => updateSettings({ warmupDurationSec: sec })}
                    >
                      {t('settings.seconds', { n: sec })}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {settings.warmupEnabled && (
              <div className="card setting-row">
                <div>
                  <div className="label">{t('settings.warmupStart')}</div>
                  <div className="hint">
                    {WARMUP_START_PCT.min}–{WARMUP_START_PCT.max}%
                  </div>
                </div>
                <div className="row-controls">
                  <input
                    type="range"
                    aria-label={t('settings.warmupStart')}
                    min={WARMUP_START_PCT.min}
                    max={WARMUP_START_PCT.max}
                    step={5}
                    value={settings.warmupStartPct}
                    onChange={(e) => updateSettings({ warmupStartPct: Number(e.target.value) })}
                  />
                  <span>{settings.warmupStartPct}%</span>
                </div>
              </div>
            )}
          </div>

          {/* Display */}
          <div className="setting-group">
            <div className="card setting-row">
              <span className="label">{t('settings.theme')}</span>
              <div className="seg">
                {(['dark', 'light', 'amoled'] as const).map((theme) => (
                  <button
                    key={theme}
                    className={settings.theme === theme ? 'active' : ''}
                    onClick={() => updateSettings({ theme })}
                  >
                    {t(`settings.theme.${theme}`)}
                  </button>
                ))}
              </div>
            </div>
            <div className="card setting-row">
              <div>
                <div className="label">{t('settings.font')}</div>
                <div className="hint">{t('settings.fontDerivation')}</div>
                {settings.font === 'dyslexic' && (
                  <div className="hint">{t('settings.fontNote')}</div>
                )}
              </div>
              <FontSelect />
            </div>
            <div className="card setting-row">
              <span className="label">{t('settings.fontSize')}</span>
              <div className="seg">
                {([0, 1, 2, 3] as const).map((size) => (
                  <button
                    key={size}
                    className={settings.fontSize === size ? 'active' : ''}
                    style={{ fontSize: `${0.75 + size * 0.12}rem` }}
                    onClick={() => updateSettings({ fontSize: size })}
                  >
                    A
                  </button>
                ))}
              </div>
            </div>
            <div className="card setting-row">
              <span className="label">{t('settings.orpColor')}</span>
              <div className="row-controls">
                <input
                  type="color"
                  aria-label={t('settings.orpColor')}
                  value={settings.orpColor ?? '#e74c3c'}
                  onChange={(e) => updateSettings({ orpColor: e.target.value })}
                />
                {settings.orpColor !== null && (
                  <button
                    className="btn btn-sm btn-ghost"
                    onClick={() => updateSettings({ orpColor: null })}
                  >
                    ↺
                  </button>
                )}
              </div>
            </div>
            <div className="card setting-row">
              <span className="label">{t('settings.showGuides')}</span>
              <Toggle
                value={settings.showGuides}
                onChange={(v) => updateSettings({ showGuides: v })}
              />
            </div>
          </div>

          {/* Chunking */}
          <div className="setting-group">
            <div className="card setting-row">
              <span className="label">{t('settings.chunk')}</span>
              <div className="seg">
                {([1, 2, 3] as const).map((size) => (
                  <button
                    key={size}
                    className={settings.chunkSize === size ? 'active' : ''}
                    onClick={() => updateSettings({ chunkSize: size })}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>
            {settings.chunkSize > 1 && (
              <div className="card setting-row">
                <span className="label">{t('settings.chunkLayout')}</span>
                <div className="seg">
                  {(['vertical', 'horizontal'] as const).map((layout) => (
                    <button
                      key={layout}
                      className={settings.chunkLayout === layout ? 'active' : ''}
                      onClick={() => updateSettings({ chunkLayout: layout })}
                    >
                      {t(`settings.chunkLayout.${layout}`)}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Language */}
          <div className="setting-group">
            <div className="card setting-row">
              <span className="label">{t('settings.appLanguage')}</span>
              <div className="seg">
                {(['en', 'tr'] as const).map((lang) => (
                  <button
                    key={lang}
                    className={settings.appLanguage === lang ? 'active' : ''}
                    onClick={() => updateSettings({ appLanguage: lang })}
                  >
                    {lang.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="setting-group">
            <div className="card">
              <div className="label" style={{ marginBottom: 12 }}>
                {t('settings.stats')}
              </div>
              {totals !== null && (
                <>
                  {totals.last !== undefined && (
                    <>
                      <div className="hint" style={{ marginBottom: 6 }}>
                        {t('settings.stats.session')}
                      </div>
                      <div className="stat-grid" style={{ marginBottom: 12 }}>
                        <div className="stat">
                          <div className="value">{totals.last.words}</div>
                          <div className="label">{t('settings.stats.words')}</div>
                        </div>
                        <div className="stat">
                          <div className="value">{minutes(totals.last.activeMs)}</div>
                          <div className="label">{t('settings.stats.time')}</div>
                        </div>
                        <div className="stat">
                          <div className="value">{totals.last.avgWpm}</div>
                          <div className="label">{t('settings.stats.avgWpm')}</div>
                        </div>
                      </div>
                    </>
                  )}
                  <div className="hint" style={{ marginBottom: 6 }}>
                    {t('settings.stats.total')}
                  </div>
                  <div className="stat-grid">
                    <div className="stat">
                      <div className="value">{totals.words.toLocaleString()}</div>
                      <div className="label">{t('settings.stats.words')}</div>
                    </div>
                    <div className="stat">
                      <div className="value">{minutes(totals.timeMs)}</div>
                      <div className="label">{t('settings.stats.time')}</div>
                    </div>
                    <div className="stat">
                      <div className="value">{totals.sessions}</div>
                      <div className="label">{t('settings.stats.sessions')}</div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Backup */}
          <div className="setting-group">
            <div className="card setting-row">
              <span className="label">{t('settings.backup')}</span>
              <div className="row-controls">
                <button className="btn btn-sm" onClick={() => void doExport()}>
                  {t('settings.export')}
                </button>
                <button className="btn btn-sm" onClick={() => backupInput.current?.click()}>
                  {t('settings.import')}
                </button>
              </div>
            </div>
          </div>

          <p style={{ color: 'var(--textDim)', fontSize: '0.75rem', textAlign: 'center' }}>
            Vif {APP_VERSION}
          </p>
        </div>
      </main>

      <input
        ref={backupInput}
        type="file"
        accept=".vif,.zip"
        style={{ display: 'none' }}
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file !== undefined) setImportConfirm(file);
          event.target.value = '';
        }}
      />

      {importConfirm !== null && (
        <Modal title={t('settings.import')} onClose={() => setImportConfirm(null)}>
          <p style={{ color: 'var(--textMuted)', lineHeight: 1.6 }}>
            {t('settings.importConfirm')}
          </p>
          <div className="modal-actions">
            <button className="btn" onClick={() => setImportConfirm(null)}>
              {t('common.cancel')}
            </button>
            <button
              className="btn btn-primary"
              onClick={() => {
                const file = importConfirm;
                setImportConfirm(null);
                void doImport(file);
              }}
            >
              {t('common.ok')}
            </button>
          </div>
        </Modal>
      )}

      {toast !== null && <div className="toast">{toast}</div>}
    </div>
  );
}

function Toggle({
  value,
  onChange,
}: {
  value: boolean;
  onChange: (v: boolean) => void;
}): ReactNode {
  const { t } = useServices();
  return (
    <div className="seg">
      <button className={value ? 'active' : ''} onClick={() => onChange(true)}>
        {t('common.on')}
      </button>
      <button className={!value ? 'active' : ''} onClick={() => onChange(false)}>
        {t('common.off')}
      </button>
    </div>
  );
}
